import { Controller } from "@hotwired/stimulus"

// Every clamp on the page measures in one animation frame: all the reads, then all the writes.
// Measuring in connect() forced a layout per card, and a long thread connects dozens of cards in
// the one task that renders it — so the reader's next click waited behind all of them.
const queued = new Set()
let scheduled = null

function measureQueued() {
  scheduled = null
  const clamps = [ ...queued ].filter((clamp) => clamp.element.isConnected && !clamp.isOpen)
  queued.clear()

  const overflows = clamps.map((clamp) => clamp.overflows())
  clamps.forEach((clamp, index) => clamp.apply(overflows[index]))
}

// Clips a tall inbound body to a preview and offers "Show full message" only when the
// preview actually overflows. Separate from `disclosure`: that controller hides its
// panel with `hidden`, which would bury the new words the clamp exists to keep visible.
export default class extends Controller {
  static targets = ["content", "toggle"]

  connect() {
    this.sync()
    this.contentTarget.addEventListener("load", this.sync, true)
  }

  disconnect() {
    this.contentTarget.removeEventListener("load", this.sync, true)
    queued.delete(this)
  }

  toggle() {
    this.setOpen(!this.isOpen)
  }

  get isOpen() {
    return this.element.classList.contains("is-open")
  }

  setOpen(open) {
    this.element.classList.toggle("is-open", open)
    this.toggleTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  // The toggle is server-rendered hidden, so a card waiting for its frame shows no button rather
  // than a wrong one.
  sync = () => {
    queued.add(this)
    scheduled ??= requestAnimationFrame(measureQueued)
  }

  overflows() {
    return this.contentTarget.scrollHeight > this.contentTarget.clientHeight + 1
  }

  apply(overflows) {
    this.toggleTarget.hidden = !overflows
  }
}
